import {
    CameraServiceType,
    CameraWithInfo,
    CaptureInfoCaptureIndicator,
    Solar4gAlarmROI,
    Solar4gCamera,
    Solar4gCaptureInfo,
    Solar4gSchedule,
} from '@EcamModel/model';
import { GeoZoneOperationConfigDay } from '@EcamModel/model/GeoZoneOperationConfigDay';
import { BaseTextField } from '@components/BaseTextField';
import PopUpBase from '@components/PopUpBase';
import { IPopUp } from '@hooks/usePopUp';
import { Autocomplete, Button, Stack, TextField, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { cloneDeep } from 'lodash';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import moment, { Moment } from 'moment';
import { AiOutlineMinus, AiOutlinePlus } from 'react-icons/ai';
import BaseTimePicker from './BaseTimePicker';
import { NewItem } from './CaptureManagementMilesightCameraSolar';
import InformationIcon from './icon/InformationIcon';
import { useCameraDetailContext } from '../../hook/CameraDetailsProvider';
import useMileSightSolar from '@pages/cameras/hook/useMilesightSolar';
import useMedia from '@hooks/useMedia';

type Props = Omit<IPopUp, 'onConfirm'> & {
    handleUpdateCapture: (data: Partial<Solar4gCamera>) => Promise<void>;
    handleUpdateCaptureROI: (data: Partial<Solar4gAlarmROI>) => Promise<void>;
    handleUpdateCaptureSchedule: (data: Partial<Solar4gSchedule>) => Promise<void>;
    updatedTimeFields: NewItem[];
    timeFields: NewItem[];
    setTimeFields: Dispatch<SetStateAction<NewItem[]>>;
    camera: CameraWithInfo;
};

export type OperationHours = {
    _fakeId?: string;
    From?: Date;
    To?: Date;
} & Omit<Partial<GeoZoneOperationConfigDay>, 'From' | 'To'>;

type FormValues = {
    From: Moment;
    To: Moment;
};

export default function PopUpEditTriggerCapture(props: Props) {
    const { open, onClose, updatedTimeFields, timeFields, setTimeFields, camera } = props;

    const { Solar4gCaptureInfo, Solar4gSchedule, Solar4gAlarmROI } = useMileSightSolar();

    const { isMediumMD, isMobileSM } = useMedia()

    const [isCheckValueTrigger, setIsCheckValueTrigger] = useState<boolean>(false);

    const handleAddShifts = () => {
        setTimeFields((prev) => [...prev, { id: updatedTimeFields.length, fromTime: '', toTime: '' }]);
    };

    const handleRemoveShifts = (indexNumber: number) => {
        const _prevState = cloneDeep(timeFields);
        const updatedTimeFields = _prevState.filter((item, index) => index !== indexNumber);
        setTimeFields(updatedTimeFields);
    };

    const {
        control,
        setValue,
        reset,
        handleSubmit,
        clearErrors,
        watch,
        formState: { isDirty, isValid, errors },
    } = useForm<Partial<Solar4gCaptureInfo & Solar4gAlarmROI & Solar4gSchedule & FormValues>>({
        mode: 'all',
        defaultValues: {
            Threshold: 0,
            Interval: 0,
            Indicator: 0,
            // LowSpeedLimit: 0,
            HighSpeedLimit: 0,
            // RadarSensitivity: 0,
            // CaptureTimeout: 0,
        },
    });
    const disabled = isCheckValueTrigger ? !isCheckValueTrigger : !(isValid && isDirty);

    const defaultValues: Partial<Solar4gCaptureInfo & Solar4gAlarmROI & Solar4gSchedule> = {
        Threshold: Solar4gAlarmROI?.Threshold,
        Interval: Solar4gAlarmROI?.Interval,
        Indicator: Solar4gCaptureInfo?.Indicator,
        // LowSpeedLimit: Solar4gCaptureInfo?.LowSpeedLimit,
        HighSpeedLimit: Solar4gCaptureInfo?.HighSpeedLimit,
        Trigger: Solar4gSchedule?.Trigger,
        // RadarSensitivity: Solar4gCaptureInfo?.RadarSensitivity,
        // CaptureTimeout: Solar4gCaptureInfo?.CaptureTimeout,
    };

    useEffect(() => {
        if (!open) return;
        reset(defaultValues);
        setTimeFields(updatedTimeFields);
    }, [open]);

    useEffect(() => {
        const timePrev = cloneDeep(timeFields);

        const sendDataTrigger = timePrev.map((item) => {
            return timePrev.length > 0
                ? `${item.fromTime ? item.fromTime : '00:00'}:${item.toTime ? item.toTime : '23:59'}:1`
                : ``;
        });

        if (Solar4gSchedule?.Trigger !== `${sendDataTrigger.join(';')}`) {
            setIsCheckValueTrigger(true);
        } else {
            setIsCheckValueTrigger(false);
        }
    }, [timeFields]);

    const handleEditTriggerCapture = async (data: Partial<Solar4gCaptureInfo & Solar4gAlarmROI & Solar4gSchedule>) => {
        const timePrev = cloneDeep(timeFields);

        const sendDataTrigger = timePrev.map((item) => {
            return timePrev.length > 0
                ? `${item.fromTime ? item.fromTime : '00:00'}:${item.toTime ? item.toTime : '23:59'}:1`
                : ``;
        });

        // data.Trigger = `${sendDataTrigger.join(';')}`;

        const _editTriggerCapture: Solar4gCamera = {} as Solar4gCamera;
        for (const key in data) {
            if (data[key] !== defaultValues[key]) {
                _editTriggerCapture[key] = data[key];
            }
        }

        if (Solar4gAlarmROI?.Interval !== data.Interval || Solar4gAlarmROI?.Threshold !== data.Threshold) {
            await props.handleUpdateCaptureROI(_editTriggerCapture);
        }
        // if (Solar4gSchedule?.Trigger !== data.Trigger) {
        //     await props.handleUpdateCaptureSchedule(_editTriggerCapture);
        // }
        if (
            Solar4gCaptureInfo?.Indicator !== data.Indicator ||
            // Solar4gCaptureInfo?.LowSpeedLimit !== data.LowSpeedLimit ||
            Solar4gCaptureInfo?.HighSpeedLimit !== data.HighSpeedLimit
            // Solar4gCaptureInfo?.RadarSensitivity !== data.RadarSensitivity ||
            // Solar4gCaptureInfo?.CaptureTimeout !== data.CaptureTimeout
        ) {
            await props.handleUpdateCapture(_editTriggerCapture);
        }
        props.onClose?.();
    };

    const validateSpeedLimits = (value) => {
        const highSpeedLimitValue = Number(watch('HighSpeedLimit'));
        const lowSpeedLimitValue = Number(watch('LowSpeedLimit'));

        if (!isNaN(highSpeedLimitValue) && !isNaN(lowSpeedLimitValue)) {
            if (value >= lowSpeedLimitValue && value <= highSpeedLimitValue) {
                return true;
            } else {
                return 'Maximum detection speed must be greater than or equal to minimum detection speed.';
            }
        }
        return true;
    };

    return (
        <PopUpBase
            open={open}
            onClose={onClose}
            onConfirm={() => { }}
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        maxWidth: '860px',
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            title={
                <Stack>
                    <Typography variant="h4">Trigger capture</Typography>
                </Stack>
            }
            titleProps={{ '&.MuiDialogTitle-root': { paddingTop: '24px' } }}
            subTitleProps={{ sx: { color: 'gray' } }}
            minWidthButton={150}
            desc={
                <Stack width="100%" sx={{ background: '#FAFAFA', p: 2 }} spacing={3}>
                    {/* Al confidence: */}
                    <Grid xs={12} container alignItems="center" gap={isMobileSM ? 2 : 0} wrap='nowrap'>
                        <Grid xs={3.5}>
                            <Typography sx={{ width: 240 }}>Al confidence:</Typography>
                        </Grid>
                        <Grid container xs={8.5} justifyContent={'space-between'} gap={isMobileSM ? 2 : 0} wrap='nowrap'>
                            <Grid xs={9}>
                                <Controller
                                    name="Threshold"
                                    control={control}
                                    rules={{
                                        maxLength: {
                                            value: 10,
                                            message: '',
                                        },
                                        minLength: {
                                            value: 1,
                                            message: '',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <BaseTextField
                                            {...field}
                                            value={field.value}
                                            onChange={(e) => {
                                                const value = Number(e.target.value);
                                                if (!isNaN(value)) {
                                                    field.onChange(isNaN(Number(value)) ? value : Number(value));
                                                }
                                            }}
                                            placeholder={'Al confidence'}
                                            error={!!error?.message}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid md={3} textAlign={'end'}>
                                <Button
                                    variant="cancelSmall"
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        fontSize: 12,
                                        '&.MuiButtonBase-root': {
                                            marginTop: '7px !important',
                                        },
                                    }}
                                    onClick={() => {
                                        setValue('Threshold', 5, { shouldDirty: true, shouldValidate: true });
                                    }}
                                >
                                    Reset
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* Sensor sensitivity: */}
                    <Grid xs={12} container>
                        <Grid xs={3.5}>
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <Typography>Sensor sensitivity:</Typography>
                                {!isMediumMD && <Tooltip placement="top" title="The system should auto-correct the capture config.">
                                    <InformationIcon />
                                </Tooltip>}
                            </Stack>
                        </Grid>
                        <Grid container xs={8.5} justifyContent={'space-between'}>
                            <Grid
                                xs={12}
                            >
                                <Controller
                                    name="RadarSensitivity"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => {
                                        return (
                                            <BaseTextField
                                                {...field}
                                                value={field.value}
                                                disabled
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);
                                                    if (!isNaN(value)) {
                                                        field.onChange(isNaN(Number(value)) ? value : Number(value));
                                                    }
                                                }}
                                                placeholder={'Sensor sensitivity'}
                                                error={!!error?.message}
                                                helperText={error?.message}
                                            />
                                        );
                                    }}
                                />
                            </Grid>
                            {/* <Grid md={2} textAlign={'end'}>
                                <Button
                                    variant="cancelSmall"
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        fontSize: 12,
                                        '&.MuiButtonBase-root': {
                                            marginTop: '7px !important',
                                        },
                                    }}
                                    onClick={() => {
                                        setValue('RadarSensitivity', 4, { shouldDirty: true, shouldValidate: true });
                                    }}
                                >
                                    Reset
                                </Button>
                            </Grid> */}
                        </Grid>
                    </Grid>
                    {/* Minimum detection speed: */}
                    <Grid xs={12} container>
                        <Grid xs={3.5}>
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <Typography >Minimum detection speed:</Typography>
                                {!isMediumMD && <Tooltip
                                    placement="top"
                                    // title="Targets with a speed smaller than this set value will trigger the capture."
                                    title="The system should auto-correct the capture config."
                                >
                                    <InformationIcon />
                                </Tooltip>}
                            </Stack>
                        </Grid>
                        <Grid container xs={8.5} justifyContent={'space-between'}>
                            <Grid xs={12}>
                                <Controller
                                    name="LowSpeedLimit"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <BaseTextField
                                            {...field}
                                            disabled
                                            onChange={(e) => {
                                                const value = Number(e.target.value);
                                                if (!isNaN(value)) {
                                                    field.onChange(isNaN(Number(value)) ? value : Number(value));
                                                }
                                                setValue('HighSpeedLimit', watch().HighSpeedLimit, {
                                                    shouldValidate: true,
                                                });
                                            }}
                                            maxLength={2}
                                            placeholder={'Minimum detection speed'}
                                            error={!!error?.message}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            {/* <Grid md={2}>
                                <Typography sx={{ lineHeight: '40px' }} textAlign={'end'}>
                                    {' '}
                                    Km/h (0~70)
                                </Typography>
                            </Grid> */}
                        </Grid>
                    </Grid>

                    {/* Maximum detection speed: */}
                    <Grid xs={12} container>
                        <Grid xs={3.5}>
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <Typography>Maximum detection speed:</Typography>
                                {!isMediumMD && <Tooltip
                                    placement="top"
                                    title="Targets with a speed greater than this set value will trigger the capture."
                                >
                                    <InformationIcon />
                                </Tooltip>}
                            </Stack>
                        </Grid>
                        <Grid container xs={8.5} justifyContent={'space-between'}>
                            <Grid xs={isMobileSM ? 12 : 9}>
                                <Controller
                                    name="HighSpeedLimit"
                                    control={control}
                                    rules={{
                                        max: {
                                            message: 'Value must be less than or equal to 70.',
                                            value: 70,
                                        },
                                        validate: validateSpeedLimits,
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <BaseTextField
                                            {...field}
                                            onChange={(e) => {
                                                const value = Number(e.target.value);
                                                if (!isNaN(value)) {
                                                    field.onChange(isNaN(Number(value)) ? value : Number(value));
                                                    setValue('LowSpeedLimit', watch().LowSpeedLimit, {
                                                        shouldValidate: true,
                                                    });
                                                }
                                            }}
                                            maxLength={2}
                                            placeholder={'Maximum detection speed'}
                                            error={!!error?.message}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid md={3}>
                                <Typography lineHeight={isMobileSM ? 'auto' : '40px'} mt={isMobileSM ? 1 : 0} textAlign={'end'}>
                                    {' '}
                                    Km/h (0~70)
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* Sleep delay: */}
                    <Grid xs={12} container alignItems={'center'}>
                        <Grid xs={3.5}>
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <Typography>Sleep delay</Typography>
                                {!isMediumMD && <Tooltip
                                    placement="top"
                                    title="The system should auto-correct the capture config."
                                // title="This value represents the duration of time after which the device enters sleep mode when no target is detected. To avoid missed detections at lower target speeds, it is recommended to configure a larger value for this setting."
                                >
                                    <InformationIcon />
                                </Tooltip>}
                            </Stack>
                        </Grid>
                        <Grid xs={8.5}>
                            <Controller
                                name="CaptureTimeout"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        disabled
                                        options={Array.from({ length: 30 }, (_, index) => index + 1).map(
                                            (second) => second
                                        )}
                                        sx={{
                                            '.MuiInputBase-root': {
                                                height: '40px',
                                            },
                                            '.MuiOutlinedInput-root .MuiAutocomplete-input': {
                                                padding: '0px',
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Sleep delay"
                                                sx={{
                                                    '.MuiFormLabel-root': {
                                                        marginTop: '-5px',
                                                    },
                                                }}
                                            />
                                        )}
                                        getOptionLabel={(o) => `${o}s`}
                                        value={field.value}
                                        onChange={(_, newValue) => {
                                            field.onChange(newValue);
                                            if (newValue === null) {
                                                setValue('CaptureTimeout', Solar4gCaptureInfo!.CaptureTimeout);
                                            }
                                        }}
                                        clearIcon={true}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    {/* Capture interval: */}
                    <Grid xs={12} container>
                        <Grid xs={3.5}>
                            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                                <Typography >Capture interval</Typography>
                                {!isMediumMD && <Tooltip
                                    placement="top"
                                    title={
                                        camera.ServiceType === CameraServiceType.Solar4G
                                            ? "Editing the 'Capture interval' is not available when the camera service type is set to 'eCam TechOps'."
                                            : 'In scenarios where the target moves slowly, setting a longer capture interval can prevent frequent triggering of captures for the same target.'
                                    }
                                >
                                    <InformationIcon />
                                </Tooltip>}
                            </Stack>
                        </Grid>
                        <Grid container xs={8.5} justifyContent={'space-between'}>
                            <Grid xs={isMobileSM ? 12 : 9}>
                                <Controller
                                    name="Interval"
                                    control={control}
                                    rules={{
                                        min: {
                                            message: 'Value must be greater than or equal to 100.',
                                            value: 100,
                                        },
                                        max: {
                                            message: 'Value must be less than or equal to 15000.',
                                            value: 15000,
                                        },
                                        pattern: {
                                            value: /^[0-9]+$/,
                                            message: 'Please enter a valid Bytes.',
                                        },
                                    }}
                                    render={({ field, fieldState: { error } }) => (
                                        <BaseTextField
                                            {...field}
                                            value={field.value}
                                            disabled={
                                                camera.ServiceType === CameraServiceType.Solar4G ? true : false
                                            }
                                            onChange={(e) => {
                                                const value = Number(e.target.value);
                                                if (!isNaN(value)) {
                                                    field.onChange(isNaN(Number(value)) ? value : Number(value));
                                                }
                                            }}
                                            placeholder={'Capture interval'}
                                            error={!!error?.message}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid xs={isMobileSM ? 12 : 3} textAlign={isMobileSM ? 'start' : 'end'} mt={isMobileSM ? 1 : 0}>
                                <Typography lineHeight={isMobileSM ? 'auto' : '40px'}>Ms (100~15000)</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* Indicator light: */}
                    <Grid xs={12} container alignItems={'center'} gap={1} wrap='nowrap'>
                        <Grid xs={isMobileSM ? 3 : 3.5}>
                            <Typography>Indicator light:</Typography>
                        </Grid>
                        <Grid xs={9}>
                            <Controller
                                name="Indicator"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        options={[0, 1]}
                                        sx={{
                                            '.MuiInputBase-root': {
                                                height: '40px',
                                            },
                                            '.MuiOutlinedInput-root .MuiAutocomplete-input': {
                                                padding: '0px',
                                            },
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select indicator light"
                                                sx={{
                                                    '.MuiFormLabel-root': {
                                                        marginTop: '-5px',
                                                    },
                                                }}
                                            />
                                        )}
                                        getOptionLabel={(options) => {
                                            const authTypeDisplayValue = {
                                                [CaptureInfoCaptureIndicator.OFF]: 'Off',
                                                [CaptureInfoCaptureIndicator.ON]: 'On',
                                            };
                                            return authTypeDisplayValue[options];
                                        }}
                                        value={field.value}
                                        onChange={(event, newValue) => {
                                            field.onChange(newValue);
                                            // if (newValue === null) {
                                            //     setValue('Indicator', Solar4gCaptureInfo!.Indicator);
                                            // }
                                        }}
                                        clearIcon={true}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>


                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="cancel"
                        onClick={() => {
                            setTimeFields(updatedTimeFields);
                            props.onClose?.();
                            clearErrors();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="contained"
                        disabled={disabled}
                        onClick={handleSubmit(async (data) => {
                            await handleEditTriggerCapture(data);
                        })}
                    >
                        Save
                    </Button>
                </Stack>
            }
        />
    );
}
